import { datadogRum } from '@datadog/browser-rum';
import { installReplayUrlGuard } from './replayUrlGuard';
import {
  getJourneyAttribution,
  clearJourneyAttribution
} from './rumAttribution';
import {
  installJourneyInteractions,
  setJourneySink,
  safeJourneyContext,
  resetJourneyObservationCache
} from './journeyInstrumentation';

const CONSENT = 'jeddle_rum_consent_staging';
const JOURNEY = 'jeddle_rum_journey_staging';
const INCOMING_IDENTITY = 'jeddle_rum_incoming_identity_staging';
const DOMAIN = '; Domain=staging.jeddle.com; Path=/; Secure; SameSite=Lax';
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SAFE_ACTIONS = new Set([
  'jeddai_arrived',
  'login_succeeded',
  'signup_completed',
  'purchase_confirmed',
  'jeddai_interaction',
  'jeddai_operation'
]);
const STATIC_SEGMENTS = new Set(
  'docs tools markingcriterias rubrics getFeedback feedbackRequest documentsReview giveFeedback completed feedbackHistory class-insights classes-insights classes tasks upload start sharedresponses exemplarResponses sharedtasks sharedmarkingtools advanced-marking view submissions review documents drafts commentbanks new successcriterias shortanswers advanced-controls jeddai onboarding marking exams billing return m home account library notifications samples new-draft draft drafts requests submissions new-task review upload response task settings classes insights marking-tools teacher-onboarding student-onboarding non-school-teacher-onboarding'.split(
    ' '
  )
);
let initialized = false;
let active = false;
let installed = false;
let arrived = false;
let authenticated = false;
let profileId;
let replayGuardFailed = false;
let activeJourneyId;
let locallyRemovedJourney = false;
let externalJourneyDuringDeny = false;
let identityGeneration = 0;
export const getStagingIdentityGeneration = () => ({
  generation: identityGeneration,
  journey: cookie(JOURNEY)
});
export const isCurrentStagingIdentityGeneration = (snapshot) => {
  if (!snapshot || snapshot.generation !== identityGeneration) return false;
  const current = cookie(JOURNEY);
  if (
    activeJourneyId &&
    current !== activeJourneyId &&
    !(locallyRemovedJourney && !current)
  )
    return false;
  return (
    current === snapshot.journey ||
    (!snapshot.journey && current === activeJourneyId) ||
    (locallyRemovedJourney && !current)
  );
};
let incomingIdentityPending = false;
let previousIncomingId;
let incomingConsentOutstanding = false;
try {
  if (window.location.hostname === 'go.staging.jeddle.com') {
    const pending = localStorage.getItem(INCOMING_IDENTITY);
    if (pending !== null) {
      incomingIdentityPending = true;
      previousIncomingId = pending === 'anonymous' ? undefined : pending;
    }
  }
} catch {
  incomingIdentityPending = true;
}

export const isStagingRumHost = () =>
  window.location.hostname === 'go.staging.jeddle.com';
const cookie = (name) =>
  document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);
const writeCookie = (name, value, age) => {
  document.cookie = `${name}=${value}; Max-Age=${age}${DOMAIN}`;
};
const recordingAllowed = () => {
  const choice = cookie(CONSENT);
  return choice === undefined || choice === '' || choice === 'allow.v1';
};
export const sensitiveLocation = () => {
  try {
    const { pathname, hash, search } = window.location;
    const hashPath = hash.startsWith('#/') ? hash.slice(1) : '';
    const path = decodeURIComponent((hashPath || pathname).split('?')[0]);
    if (
      /^(?:\/(?:check|thanks|gift|r|sr)(?:\/|$)|\/m\/upload(?:\/|$)|\/view\/submissions(?:\/|$))/.test(
        path
      )
    )
      return true;
    const query = new URLSearchParams(search);
    const hashQuery = new URLSearchParams(
      hashPath.split('?')[1] || (hash.startsWith('#?') ? hash.slice(2) : '')
    );
    const secretKeys = new Set(['sso_token', 'lti_code', 'code', 'token', 'v']);
    return [...query.keys(), ...hashQuery.keys()].some((key) =>
      secretKeys.has(key.toLowerCase())
    );
  } catch {
    return true;
  }
};
const journey = () => {
  let id = cookie(JOURNEY);
  if (!UUID.test(id || '')) {
    if (!window.crypto?.randomUUID) return undefined;
    id = window.crypto.randomUUID();
  }
  writeCookie(JOURNEY, id, 1800);
  return cookie(JOURNEY) === id ? id : undefined;
};
export const routeTemplate = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    const route = parsed.hash.startsWith('#/')
      ? parsed.hash.slice(1).split('?')[0]
      : parsed.pathname;
    const safe = route
      .split('/')
      .map((part) => (!part || STATIC_SEGMENTS.has(part) ? part : ':id'))
      .join('/');
    return `${parsed.origin}/#${safe || '/'}`;
  } catch {
    return '';
  }
};
export const stagingBeforeSend = (event) => {
  try {
    if (!event) return false;
    // Views cannot be discarded by the SDK, so sanitize them even during revocation.
    if (event.view) {
      const safeUrl = routeTemplate(event.view.url);
      event.view.url = safeUrl;
      event.view.referrer = '';
      event.view.name = safeUrl.split('#')[1];
      if (event.view.performance?.lcp?.resource_url)
        event.view.performance.lcp.resource_url = '';
    }
    const actionContext =
      event.type === 'action'
        ? safeJourneyContext(event.action?.target?.name, event.context)
        : null;
    event.context = {};
    // Never trust a persisted SDK user during a new authentication exchange.
    event.usr =
      active &&
      !incomingIdentityPending &&
      recordingAllowed() &&
      !sensitiveLocation() &&
      cookie(JOURNEY) === activeJourneyId &&
      profileId
        ? { id: String(profileId) }
        : {};
    if (
      !recordingAllowed() ||
      sensitiveLocation() ||
      !active ||
      cookie(JOURNEY) !== activeJourneyId
    )
      return event.type === 'view';
    const id = journey();
    if (!id) return event.type === 'view';
    // Error text, custom context and arbitrary action names can contain submitted work.
    if (!['view', 'action', 'error', 'long_task'].includes(event.type))
      return false;
    if (event.type === 'error') {
      if (
        !event.error ||
        (event.error.type &&
          ![
            'Error',
            'TypeError',
            'ReferenceError',
            'SyntaxError',
            'RangeError',
            'URIError',
            'EvalError'
          ].includes(event.error.type)) ||
        event.error.causes?.length ||
        event.error.component_stack
      )
        return false;
      event.error.message = 'Error details masked';
      event.error.stack = '';
      event.error.handling_stack = '';
      event.error.fingerprint = '';
      if (event.error.resource) event.error.resource.url = '';
    }
    if (event.type === 'long_task') {
      (event.long_task?.scripts || []).forEach((script) => {
        script.source_url = '';
        script.invoker = '';
      });
    }
    if (
      event.type === 'action' &&
      !SAFE_ACTIONS.has(event.action?.target?.name)
    )
      return false;
    if (
      ['jeddai_interaction', 'jeddai_operation'].includes(
        event.action?.target?.name
      ) &&
      !actionContext
    )
      return false;
    event.context = {
      journey_id: id,
      surface: 'jeddai',
      schema_version: 3,
      instrumentation_release: 'scorecard-v1',
      ...getJourneyAttribution(id),
      ...(actionContext || {}),
      ...(actionContext
        ? { route: routeTemplate(window.location.href).split('#')[1] }
        : {})
    };
    return true;
  } catch {
    return false;
  }
};
const call = (fn) => {
  try {
    fn();
  } catch {
    /* Telemetry must not break the app. */
  }
};
const consumeMarkers = () => {
  if (!active || !authenticated) return;
  [
    ['login', 'login_succeeded'],
    ['signup', 'signup_completed'],
    ['purchase', 'purchase_confirmed']
  ].forEach(([kind, action]) => {
    const name = `jeddle_rum_${kind}_staging`;
    if (cookie(name) === 'success.v1') {
      writeCookie(name, '', 0);
      call(() => datadogRum.addAction(action));
    }
  });
};
const sync = () => {
  if (incomingIdentityPending && recordingAllowed()) return;
  const currentConsent = cookie(CONSENT);
  const shouldTrack =
    recordingAllowed() && !sensitiveLocation() && !replayGuardFailed;
  if (!shouldTrack) {
    if (active || incomingConsentOutstanding) {
      incomingConsentOutstanding = false;
      call(() => datadogRum.stopSessionReplayRecording());
      call(() => datadogRum.setTrackingConsent('not-granted'));
    }
    active = false;
    if (!recordingAllowed()) {
      call(clearJourneyAttribution);
      resetJourneyObservationCache();
      if (currentConsent === 'deny.v1') {
        if (cookie(JOURNEY) && cookie(JOURNEY) === activeJourneyId)
          locallyRemovedJourney = true;
        else if (cookie(JOURNEY) && activeJourneyId)
          externalJourneyDuringDeny = true;
        writeCookie(JOURNEY, '', 0);
        ['login', 'signup', 'purchase'].forEach((kind) =>
          writeCookie(`jeddle_rum_${kind}_staging`, '', 0)
        );
      }
      arrived = false;
    }
    return;
  }
  const priorCookie = cookie(JOURNEY);
  const id = journey();
  if (!id) return;
  const ownConsentReset =
    locallyRemovedJourney && !externalJourneyDuringDeny && !priorCookie;
  locallyRemovedJourney = false;
  externalJourneyDuringDeny = false;
  if (activeJourneyId && activeJourneyId !== id && !ownConsentReset) {
    identityGeneration++;
    resetJourneyObservationCache();
    profileId = undefined;
    authenticated = false;
    arrived = false;
    call(() => datadogRum.clearUser());
    if (initialized) call(() => datadogRum.stopSession());
  }
  activeJourneyId = id;
  const wasActive = active;
  active = true; // beforeSend can run synchronously during init
  if (!initialized) {
    try {
      if (
        !installReplayUrlGuard(() => {
          replayGuardFailed = true;
          active = false;
          call(() => datadogRum.stopSessionReplayRecording());
          call(() => datadogRum.setTrackingConsent('not-granted'));
        })
      ) {
        active = false;
        return;
      }
      datadogRum.init({
        applicationId: 'c060e39a-fe15-41c6-b580-158c4d290665',
        clientToken: 'pub5367c2844dea48390397f6cf72864575',
        site: 'us5.datadoghq.com',
        service: 'gojeddle',
        env: 'staging',
        ...(/^[0-9a-f]{40}$/.test(process.env.REACT_APP_BUILD_SHA || '')
          ? { version: process.env.REACT_APP_BUILD_SHA }
          : {}),
        trackAnonymousUser: false,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        sessionPersistence: 'local-storage',
        trackSessionAcrossSubdomains: false,
        trackUserInteractions: false,
        trackResources: false,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask',
        beforeSend: stagingBeforeSend
      });
      initialized = true;
    } catch {
      active = false;
      return;
    }
  } else if (!wasActive) {
    call(() => datadogRum.setTrackingConsent('granted'));
    call(() => datadogRum.startSessionReplayRecording());
  }
  if (profileId) call(() => datadogRum.setUser({ id: String(profileId) }));
  if (authenticated && !arrived) {
    arrived = true;
    call(() => datadogRum.addAction('jeddai_arrived'));
  }
  consumeMarkers();
};
const safeSync = () => call(sync);
export const initStagingRum = ({ isAuthenticated = false } = {}) => {
  if (!isStagingRumHost()) return;
  if (authenticated && !isAuthenticated) {
    clearStagingRumUser();
  }
  authenticated = isAuthenticated;
  if (!installed && document.body) {
    installed = true;
    setJourneySink(
      (name, context) => {
        if (
          active &&
          recordingAllowed() &&
          !sensitiveLocation() &&
          cookie(JOURNEY) === activeJourneyId
        )
          call(() => datadogRum.addAction(name, context));
      },
      () =>
        active &&
        recordingAllowed() &&
        !sensitiveLocation() &&
        cookie(JOURNEY) === activeJourneyId,
      () =>
        isStagingRumHost() &&
        active &&
        authenticated &&
        profileId &&
        !incomingIdentityPending &&
        recordingAllowed() &&
        !sensitiveLocation() &&
        UUID.test(activeJourneyId || '') &&
        cookie(JOURNEY) === activeJourneyId
          ? { journeyId: activeJourneyId }
          : null
    );
    installJourneyInteractions();
    // Register before the SDK so route changes suspend recording first.
    ['pushState', 'replaceState'].forEach((method) => {
      const original = window.history[method];
      window.history[method] = function (...args) {
        const result = original.apply(this, args);
        safeSync();
        return result;
      };
    });
    window.addEventListener('storage', (event) => {
      if (event.key === 'jeddle_rum_consent_changed_staging') safeSync();
    });
    window.addEventListener('focus', safeSync);
    window.addEventListener('hashchange', safeSync);
    document.addEventListener('visibilitychange', safeSync);
    window.setInterval(safeSync, 1000);
  }
  safeSync();
};
export const clearStagingRumUser = ({
  preserveAnonymousJourney = false
} = {}) => {
  if (!isStagingRumHost()) return;
  identityGeneration++;
  locallyRemovedJourney = false;
  externalJourneyDuringDeny = false;
  if (incomingIdentityPending && previousIncomingId && !initialized) {
    try {
      localStorage.removeItem('_dd_s_v2');
      localStorage.removeItem('_dd_s');
      if (localStorage.getItem('_dd_s_v2') || localStorage.getItem('_dd_s'))
        replayGuardFailed = true;
    } catch {
      replayGuardFailed = true;
    }
  }
  try {
    localStorage.removeItem(INCOMING_IDENTITY);
  } catch {
    replayGuardFailed = true;
  }
  incomingIdentityPending = false;
  previousIncomingId = undefined;
  incomingConsentOutstanding = false;
  const keepJourney =
    preserveAnonymousJourney && !profileId && !authenticated && !initialized;
  profileId = undefined;
  authenticated = false;
  arrived = false;
  call(() => datadogRum.clearUser());
  if (initialized) call(() => datadogRum.stopSession());
  if (!keepJourney) {
    call(clearJourneyAttribution);
    resetJourneyObservationCache();
    activeJourneyId = undefined;
    writeCookie(JOURNEY, '', 0);
  }
};
export const beginStagingRumIncomingLogin = (priorId) => {
  if (!isStagingRumHost()) return;
  identityGeneration++;
  previousIncomingId = profileId || priorId || previousIncomingId || undefined;
  incomingIdentityPending = true;
  try {
    localStorage.setItem(
      INCOMING_IDENTITY,
      String(previousIncomingId || 'anonymous')
    );
  } catch {
    replayGuardFailed = true;
  }
  resetJourneyObservationCache();
  profileId = undefined;
  authenticated = false;
  arrived = false;
  incomingConsentOutstanding = active;
  if (active) call(() => datadogRum.stopSessionReplayRecording());
  active = false;
  call(() => datadogRum.clearUser());
};
export const setStagingRumUser = (id) => {
  const nextId = /^(?:[0-9]+|[0-9a-f]{8}-[0-9a-f-]{27,})$/i.test(String(id))
    ? id
    : undefined;
  if (incomingIdentityPending) {
    if (!nextId) return; // A token-only LTI exchange is not a verified profile.
    if (previousIncomingId && String(previousIncomingId) !== String(nextId)) {
      // stopSession before SDK init cannot clear the persisted session.
      if (!initialized) {
        try {
          localStorage.removeItem('_dd_s_v2');
          localStorage.removeItem('_dd_s');
          if (localStorage.getItem('_dd_s_v2') || localStorage.getItem('_dd_s'))
            return;
        } catch {
          return;
        }
      }
      clearStagingRumUser();
    } else {
      try {
        localStorage.removeItem(INCOMING_IDENTITY);
      } catch {
        return;
      }
      incomingIdentityPending = false;
      previousIncomingId = undefined;
      incomingConsentOutstanding = false;
    }
  }
  if (profileId && String(profileId) !== String(nextId)) {
    clearStagingRumUser();
  }
  profileId = nextId;
  if (active && profileId)
    call(() => datadogRum.setUser({ id: String(profileId) }));
};
